import axios from 'axios'
import { Message, Loading } from 'element-ui'
import store from '@/store'
import { refreshToken } from '@/api'
import CryptoJS from 'crypto-js'

const SECRET_KEY = 'jargold_bearing_mall_2025_199506'

// 请求队列相关变量
let isRefreshing = false
let failedQueue = []

// 防重复提示相关变量
let isLoginExpiredMessageShown = false
let loginExpiredMessageTimer = null

// 处理队列中的请求
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

// 重置登录过期提示状态
const resetLoginExpiredMessage = () => {
  isLoginExpiredMessageShown = false
  if (loginExpiredMessageTimer) {
    clearTimeout(loginExpiredMessageTimer)
    loginExpiredMessageTimer = null
  }
}

// 显示登录过期提示（防重复）
const showLoginExpiredMessage = () => {
  if (!isLoginExpiredMessageShown) {
    isLoginExpiredMessageShown = true
    // Message.error('登录已过期，请重新登录')
    loginExpiredMessageTimer = setTimeout(resetLoginExpiredMessage, 3000)
  }
}

/**
 * 生成加密UUID
 */
function generateUUID() {
  const userId = store.getters['user/userInfo'] ? store.getters['user/userInfo'].id : 0
  const timestamp = Date.now()
  const data = `${userId}|${timestamp}`

  const key = CryptoJS.enc.Utf8.parse(SECRET_KEY.padEnd(32, '\0').substring(0, 32))
  const encrypted = CryptoJS.AES.encrypt(data, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7
  })

  return CryptoJS.enc.Base64.stringify(encrypted.ciphertext)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

/**
 * 获取错误消息
 */
function getErrorMessage(data, defaultMsg = '请求失败') {
  // 优先使用 msg 字段，其次使用 message 字段
  return data?.msg || data?.message || defaultMsg
}

// 创建axios实例
const service = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  timeout: 10000000,
  _headers: {
    'Content-Type': 'application/json;charset=utf-8',
    'Source': 'PC'
  },
  get headers() {
    return this._headers
  },
  set headers(value) {
    this._headers = value
  },
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 添加UUID到header
    if (!config.headers['X-Request-ID']) {
      config.headers['X-Request-ID'] = generateUUID()
    }

    // 显示loading
    if (config.showLoading) {
      config.loadingInstance = Loading.service({
        lock: true,
        text: '加载中...',
        background: 'rgba(0, 0, 0, 0.7)'
      })
    }

    // 添加token到header
    const token = store.getters['user/accessToken']
    // if (token && config.requireAuth !== false) {
      config.headers.Authorization = `Bearer ${token}`
    // }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    // 关闭loading
    if (response.config.loadingInstance) {
      response.config.loadingInstance.close()
    }

    const { data } = response
    if (data.code === 1 || data.success) {
      return data
    } else {
      const errorMsg = getErrorMessage(data)
      // 使用Message显示错误信息，但返回一个已处理的错误对象而不是reject
      Message.error(errorMsg)
      // 返回一个包含错误信息的对象，而不是reject，避免未捕获的Promise错误
      return {
        code: 0,
        success: false,
        msg: errorMsg,
        data: null,
        _isError: true // 标记这是一个错误响应
      }
    }
  },
  async error => {
    // 关闭loading
    if (error.config && error.config.loadingInstance) {
      error.config.loadingInstance.close()
    }

    const { response, config } = error

    if (!response) {
      Message.error('网络错误，请检查网络连接')
      // 返回错误对象而不是reject
      return {
        code: 0,
        success: false,
        msg: '网络错误，请检查网络连接',
        data: null,
        _isError: true
      }
    }

    switch (response.status) {
      case 401:
        // 如果是刷新token的请求失败，直接退出登录
        if (config.url.includes('refreshToken')) {
          store.dispatch('user/clearUserInfo')
          showLoginExpiredMessage()
          return {
            code: 0,
            success: false,
            msg: '登录已过期，请重新登录',
            data: null,
            _isError: true
          }
        }

        // 如果没有refreshToken，直接退出登录
        const currentRefreshToken = store.getters['user/refreshToken']
        if (!currentRefreshToken) {
          store.dispatch('user/clearUserInfo')
          showLoginExpiredMessage()
          return {
            code: 0,
            success: false,
            msg: '登录已过期，请重新登录',
            data: null,
            _isError: true
          }
        }

        // 检查重试次数，最大重试1次
        const retryCount = config._retryCount || 0
        if (retryCount >= 1) {
          store.dispatch('user/clearUserInfo')
          showLoginExpiredMessage()
          return {
            code: 0,
            success: false,
            msg: '登录已过期，请重新登录',
            data: null,
            _isError: true
          }
        }

        config._retryCount = retryCount + 1

        // 如果正在刷新token，将当前请求加入队列
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          }).then(token => {
            config.headers.Authorization = `Bearer ${token}`
            return service(config)
          }).catch(err => {
            // 捕获队列中的错误，返回错误对象
            return {
              code: 0,
              success: false,
              msg: err.message || '请求失败',
              data: null,
              _isError: true
            }
          })
        }

        isRefreshing = true

        try {
          // 刷新token
          const refreshResponse = await refreshToken({ refresh_token: currentRefreshToken })

          if (refreshResponse.code === 1) {
            const { access_token, refresh_token } = refreshResponse.data

            // 同步更新token到vuex和localStorage
            if (refresh_token) {
              store.dispatch('user/setTokens', {
                accessToken: access_token,
                refreshToken: refresh_token
              })
            } else {
              store.dispatch('user/updateAccessToken', access_token)
            }

            // 处理队列中的请求
            processQueue(null, access_token)

            // 重新发送当前请求
            config.headers.Authorization = `Bearer ${access_token}`
            return service(config)
          } else {
            throw new Error(getErrorMessage(refreshResponse, '刷新token失败'))
          }
        } catch (refreshError) {
          // 刷新token失败，清除用户信息并处理队列
          store.dispatch('user/clearUserInfo')
          processQueue(refreshError, null)
          showLoginExpiredMessage()
          return {
            code: 0,
            success: false,
            msg: '登录已过期，请重新登录',
            data: null,
            _isError: true
          }
        } finally {
          isRefreshing = false
        }
        break

      case 403:
        Message.error('没有权限访问')
        return {
          code: 0,
          success: false,
          msg: '没有权限访问',
          data: null,
          _isError: true
        }

      case 500:
        Message.error('服务器内部错误')
        return {
          code: 0,
          success: false,
          msg: '服务器内部错误',
          data: null,
          _isError: true
        }

      default:
        const errorMsg = getErrorMessage(response.data, '请求失败')
        // Message.error(errorMsg)
        return {
          code: 0,
          success: false,
          msg: errorMsg,
          data: null,
          _isError: true
        }
    }
  }
)

/**
 * 统一处理请求结果
 */
function handleRequest(promise) {
  return promise.then(result => {
    // 如果结果是错误对象，确保返回统一格式
    if (result && result._isError) {
      return result
    }
    return result
  }).catch(error => {
    console.error('API请求错误:', error)
    return {
      code: 0,
      success: false,
      msg: error.message || '请求失败',
      data: null,
      _isError: true
    }
  })
}

// 封装GET请求
export const get = (url, params = {}, config = {}) => {
  return handleRequest(service({
    url,
    method: 'get',
    params,
    ...config
  }))
}

// 封装POST请求
export const post = (url, data = {}, config = {}) => {
  return handleRequest(service({
    url,
    method: 'post',
    data,
    ...config
  }))
}

// 封装PUT请求
export const put = (url, data = {}, config = {}) => {
  return handleRequest(service({
    url,
    method: 'put',
    data,
    ...config
  }))
}

// 封装DELETE请求
export const del = (url, params = {}, config = {}) => {
  return handleRequest(service({
    url,
    method: 'delete',
    params,
    ...config
  }))
}

export default service